import { Component, EventEmitter, OnInit, OnDestroy, Output, HostListener } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, timeout, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { TemplateService } from '../../../../features/templates/services/template.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit, OnDestroy {

  @Output() selectedPromptId = new EventEmitter<any>();
  prompts: any[] = [];
  displayedPrompts: any[] = [];
  selectedPrompt: any = null;
  isDropdownOpen = false;
  isMobile = false;
  isSidebarVisible = true;
  loadingPrompts = false;
  searchQuery = '';

  // Pagination state
  currentPage = 0;
  pageSize = 20;
  totalPages = 1;
  totalElements = 0;
  pageSizeOptions = [10, 20, 30, 50];

  private promptPollTimeout: any = null;
  private destroyed = false;
  private searchSubject = new Subject<string>();
  private searchSubscription: any;
  private visibilityHandler: (() => void) | null = null;

  private readonly POLL_INTERVAL = 30000; // 30 seconds instead of 10
  private readonly REQUEST_TIMEOUT = 15000; // 15 second timeout for API calls

  constructor(private templateService: TemplateService) { }

  ngOnInit(): void {
    this.checkScreen();
    this.loadCachedPrompts();
    this.fetchPrompts();

    // Live search with debounce — resets to page 0 on new search
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((query: string) => {
        this.loadingPrompts = true;
        this.currentPage = 0;
        return this.templateService.searchPromptsPageable(query, 0, this.pageSize).pipe(
          timeout(this.REQUEST_TIMEOUT),
          catchError(() => {
            this.loadingPrompts = false;
            return of(null);
          })
        );
      })
    ).subscribe({
      next: (response: any) => {
        if (response) {
          this.handlePageResponse(response);
        }
        this.loadingPrompts = false;
      },
      error: () => {
        this.loadingPrompts = false;
      }
    });

    // Pause polling when tab is hidden, resume when visible
    this.visibilityHandler = () => {
      if (document.hidden) {
        if (this.promptPollTimeout) {
          clearTimeout(this.promptPollTimeout);
          this.promptPollTimeout = null;
        }
      } else {
        // Tab became visible — fetch fresh data and resume polling
        this.fetchPrompts();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  /** Show cached prompts instantly while fresh data loads */
  private loadCachedPrompts(): void {
    try {
      const cached = sessionStorage.getItem('sidebar_prompts');
      if (cached) {
        const data = JSON.parse(cached);
        this.prompts = data;
        this.displayedPrompts = data;
      }
    } catch { }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.promptPollTimeout) {
      clearTimeout(this.promptPollTimeout);
      this.promptPollTimeout = null;
    }
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  @HostListener('window:resize')
  checkScreen() {
    this.isMobile = window.innerWidth <= 1080;
    this.isSidebarVisible = !this.isMobile;
    if (!this.isMobile) this.isDropdownOpen = false;
  }

  toggleSidebarVisibility() {
    this.isSidebarVisible = !this.isSidebarVisible;
  }

  toggleDropdown() {
    if (this.isMobile) this.isDropdownOpen = !this.isDropdownOpen;
  }

  fetchPrompts(): void {
    // Don't fetch if tab is hidden
    if (document.hidden) return;

    this.loadingPrompts = true;
    this.templateService.searchPromptsPageable(this.searchQuery, this.currentPage, this.pageSize).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError(() => {
        this.loadingPrompts = false;
        this.scheduleNextPoll();
        return of(null);
      })
    ).subscribe({
      next: (response: any) => {
        if (response) {
          this.handlePageResponse(response);
          try { sessionStorage.setItem('sidebar_prompts', JSON.stringify(this.displayedPrompts)); } catch { }
        }
        this.loadingPrompts = false;
        this.scheduleNextPoll();
      }
    });
  }

  /** Handle paginated API response */
  private handlePageResponse(response: any): void {
    if (response && response.content) {
      this.prompts = response.content;
      this.displayedPrompts = response.content;
      this.totalElements = response.totalElements || 0;
      this.totalPages = response.totalPages || 1;
      this.currentPage = response.number || 0;
    } else if (Array.isArray(response)) {
      this.prompts = response;
      this.displayedPrompts = response;
      this.totalElements = response.length;
      this.totalPages = 1;
      this.currentPage = 0;
    }
  }

  onSearchInput(): void {
    this.searchSubject.next(this.searchQuery);
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages) return;
    this.currentPage = page;
    this.fetchPrompts();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 0;
    this.fetchPrompts();
  }

  private scheduleNextPoll(): void {
    if (this.destroyed || document.hidden) return;
    if (this.promptPollTimeout) {
      clearTimeout(this.promptPollTimeout);
    }
    this.promptPollTimeout = setTimeout(() => this.fetchPrompts(), this.POLL_INTERVAL);
  }

  navigateToPrompt(promptId: any): void {
    this.selectedPrompt = this.prompts.find(p => p.id === promptId);
    this.selectedPromptId.emit(promptId);
    if (this.isMobile) this.isDropdownOpen = false;
  }
}
